import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, X, ArrowLeft, ArrowRight, Plus } from "lucide-react";
import { IMAGES } from "@/lib/site-data";
import { SectionHeading } from "@/components/SectionHeading";

export const References = () => {
  const [open, setOpen] = useState(null);

  const close = useCallback(() => setOpen(null), []);
  const step = useCallback(
    (dir) => setOpen((i) => (i === null ? null : (i + dir + IMAGES.references.length) % IMAGES.references.length)),
    []
  );

  useEffect(() => {
    if (open === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, close, step]);

  const current = open !== null ? IMAGES.references[open] : null;

  return (
    <section id="referenzen" data-testid="references-section" className="bg-[#14171a] py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="mb-14">
          <SectionHeading
            dark
            testid="references"
            eyebrow="Referenzen"
            title="Gebaut, montiert, übergeben."
            text="Eine Auswahl abgeschlossener Projekte – von der Altbausanierung bis zur fünf Meter breiten Hebe-Schiebe-Tür. Klicken Sie sich durch die Projekte."
          />
        </div>

        <div data-testid="references-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {IMAGES.references.map((r, i) => (
            <motion.button
              type="button"
              key={r.title}
              data-testid={`reference-card-${i}`}
              onClick={() => setOpen(i)}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="group relative overflow-hidden rounded-2xl h-[420px] text-left cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#e2001a]"
            >
              <img
                src={r.img}
                alt={r.title}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#14171a]/95 via-[#14171a]/20 to-transparent" />
              <span className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <Plus size={16} />
              </span>
              <div className="absolute bottom-0 inset-x-0 p-6">
                <div className="flex gap-2 mb-3">
                  {r.tags.map((t) => (
                    <span key={t} className="text-[11px] font-display font-medium tracking-wider uppercase text-white/85 bg-white/10 backdrop-blur-md border border-white/15 rounded-full px-3 py-1">
                      {t}
                    </span>
                  ))}
                </div>
                <h3 className="font-display font-semibold text-white text-xl">{r.title}</h3>
                <p className="text-white/55 text-sm mt-1 flex items-center gap-1.5">
                  <MapPin size={13} className="text-[#e2001a]" />
                  {r.place}
                </p>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {current && (
          <motion.div
            data-testid="reference-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={close}
            className="fixed inset-0 z-[100] bg-[#0c0e10]/92 backdrop-blur-xl flex items-center justify-center p-4 sm:p-10"
          >
            <button
              data-testid="lightbox-close"
              onClick={close}
              aria-label="Schließen"
              className="absolute top-5 right-5 sm:top-8 sm:right-8 w-12 h-12 rounded-full border border-white/25 text-white flex items-center justify-center hover:bg-white hover:text-[#14171a] transition-colors duration-300 z-10"
            >
              <X size={20} />
            </button>
            <button
              data-testid="lightbox-prev"
              onClick={(e) => { e.stopPropagation(); step(-1); }}
              aria-label="Vorheriges Projekt"
              className="absolute left-3 sm:left-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full border border-white/25 text-white flex items-center justify-center hover:bg-white hover:text-[#14171a] transition-colors duration-300 z-10"
            >
              <ArrowLeft size={18} />
            </button>
            <button
              data-testid="lightbox-next"
              onClick={(e) => { e.stopPropagation(); step(1); }}
              aria-label="Nächstes Projekt"
              className="absolute right-3 sm:right-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full border border-white/25 text-white flex items-center justify-center hover:bg-white hover:text-[#14171a] transition-colors duration-300 z-10"
            >
              <ArrowRight size={18} />
            </button>

            <motion.div
              key={open}
              initial={{ opacity: 0, scale: 0.96, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-5xl w-full"
            >
              <div className="relative overflow-hidden rounded-2xl">
                <img data-testid="lightbox-image" src={current.img} alt={current.title} className="w-full max-h-[62vh] object-cover" />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#14171a]/90 to-transparent h-32" />
              </div>
              <div className="mt-6 flex flex-wrap items-end justify-between gap-5">
                <div>
                  <div className="flex gap-2 mb-3">
                    {current.tags.map((t) => (
                      <span key={t} className="text-[11px] font-display font-medium tracking-wider uppercase text-white/85 bg-white/10 border border-white/15 rounded-full px-3 py-1">
                        {t}
                      </span>
                    ))}
                  </div>
                  <h3 data-testid="lightbox-title" className="font-display font-bold text-white text-2xl sm:text-3xl">{current.title}</h3>
                  <p className="text-white/55 text-sm mt-1.5 flex items-center gap-1.5">
                    <MapPin size={14} className="text-[#e2001a]" />
                    {current.place}
                  </p>
                </div>
                <div data-testid="lightbox-counter" className="font-display text-white/50 text-sm tracking-widest">
                  <span className="text-white text-xl font-semibold">0{open + 1}</span> / 0{IMAGES.references.length}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
